import {
  useDocumentMetadataMutation,
  useUpdateDocumentAssetMutation,
} from "@/hooks/assets";
import { useStore } from "@/services/store";
import FormField from "@ingka/form-field";
import { UpdateAssetDtoContentTypeEnum } from "generated";
import { useShallow } from "zustand/react/shallow";
import LabelWithToolTip from "@/components/LabelWithTooltip/LabelWithToolTip";
import InputField from "@ingka/input-field";
import { useEffect, useState } from "react";
import Label from "@ingka/label";
import { useDocumentValidation } from "../../hooks";
import { CustomSelect } from "@/components/CustomSelect/CustomSelect";
import { hasStringKey } from "@/utils/misc";

type Props = {
  assetId: number;
  language: Record<string, string>;
  documentContentTypes?: Record<string, string>;
  isLockedForOwner: boolean;
};

const Fields = ({
  assetId,
  language,
  documentContentTypes,
  isLockedForOwner,
}: Props) => {
  const updateDocumentMetadata = useDocumentMetadataMutation(assetId);
  const updateAssetsMutation = useUpdateDocumentAssetMutation(assetId);
  const { asset, resetValidationError } = useStore(
    useShallow((state) => ({
      asset: state.documentAssets[assetId],
      resetValidationError: state.resetDocumentValidationError,
    })),
  );
  const [titleValue, setTitleValue] = useState(asset?.title ?? "");
  const titleValidation = useDocumentValidation(assetId, "title");
  const languageValidation = useDocumentValidation(assetId, "language");
  const contentTypeValidation = useDocumentValidation(assetId, "contentType");

  useEffect(() => {
    setTitleValue(asset?.title ?? "");
  }, [asset?.title]);

  const selectedLanguage = asset?.language ?? "";
  const selectedContentType = asset?.contentType ?? "";

  return (
    <div className="flex flex-col">
      <Label
        htmlFor={`asset-${assetId}-title`}
        label={
          <LabelWithToolTip
            labelText="Document title"
            toolTipText="Refers to the title of the document. Since the name of the asset is automatically generated to follow naming standard the title of the document is not searchable unless added as metadata, this is the space for that."
          />
        }
      />
       <FormField {...titleValidation}>
         <InputField
           id={`asset-${assetId}-title`}
           className="max-w-[17rem]"
           type="text"
           value={titleValue}
           disabled={isLockedForOwner}
           onChange={(e) => setTitleValue(e.target.value)}
           onBlur={() => {
             updateDocumentMetadata.mutate({
               title: titleValue,
             });
             resetValidationError(assetId, "title");
           }}
         />
       </FormField>
      <Label
        htmlFor={`asset-${assetId}-language`}
        label={
          <LabelWithToolTip
            labelText="Language"
            toolTipText="Please specify which language this document is written in."
          />
        }
      />
       <FormField {...languageValidation}>
         <CustomSelect
           inputId={`asset-${assetId}-language`}
           disabled={isLockedForOwner}
           value={{
             label: language[selectedLanguage],
             value: selectedLanguage,
           }}
           onChange={(option) => {
             if (hasStringKey("value", option)) {
               updateAssetsMutation.mutate({
                 [assetId]: {
                   language: option.value,
                 },
               });

               resetValidationError(assetId, "language");
             }
           }}
           options={Object.entries(language).map(([value, label]) => ({
             value,
             label,
           }))}
         />
       </FormField>
      <Label
        htmlFor={`asset-${assetId}-type`}
        label={
          <LabelWithToolTip
            labelText="Document type"
            toolTipText="Specify what kind of document you are uploading, what its contents are. If you cannot find a suitable document type in the dropdown, please reach out to us so that we can either advise or add a new one."
          />
        }
      />
       <FormField {...contentTypeValidation}>
         <CustomSelect
           inputId={`asset-${assetId}-type`}
           disabled={isLockedForOwner}
           value={{
             label: documentContentTypes?.[selectedContentType],
             value: selectedContentType,
           }}
           onChange={(option) => {
             if (hasStringKey("value", option)) {
               updateAssetsMutation.mutate({
                 [assetId]: {
                   contentType: option.value as UpdateAssetDtoContentTypeEnum,
                 },
               });
               resetValidationError(assetId, "contentType");
             }
           }}
           options={Object.entries(documentContentTypes ?? {}).map(
             ([value, label]) => ({
               value,
               label,
             }),
           )}
         />
       </FormField>
    </div>
  );
};

export default Fields;
